import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AppraiserUser, CustomerUser, RootUser } from '@einwert/valuation-api-client'
import { getValuationApiClient } from 'ClientPortal/src/api/valuationApiClient'
import { applyPatch, assembleNameWithEmailFallback, sendToSentry, type User } from 'ComponentLibrary'
import { throwOnForbiddenApiError } from 'ClientPortal/src/api/react-query-config'
import { useContext, useMemo } from 'react'
import { IAmContext } from 'ClientPortal/src/context/IAmContext'
import { useGlobalMutationErrorHandler } from 'ClientPortal/src/api/useGlobalMutationErrorHandler'
import { getSelectedCustomer } from 'ClientPortal/src/context/SelectedCustomerContext'

type UserWithCurrentUser<TUser> = TUser & { isCurrentUser?: boolean }

export const USER_KEYS = {
    all: ['USER'],
    lists: () => [...USER_KEYS.all, 'list'],
    list: (filters: Record<string, unknown>) => [...USER_KEYS.lists(), { filters }],
    single: (userId: string | undefined) => [...USER_KEYS.all, userId],
}

function useGetCurrentUser() {
    const valuationApiClient = getValuationApiClient()
    const { data, isPending, isError } = useQuery({
        queryFn: ({ signal }) => valuationApiClient.users.getCurrentUser({ signal }),
        queryKey: USER_KEYS.all,
        throwOnError: throwOnForbiddenApiError,
    })
    return {
        user: data,
        isUserPending: isPending,
        isUserError: isError,
        isAppraiser: data?.role === 'appraiser',
    }
}

function usePatchCurrentUser() {
    const valuationApiClient = getValuationApiClient()
    const queryClient = useQueryClient()
    const handleError = useGlobalMutationErrorHandler()

    return useMutation({
        mutationFn: valuationApiClient.users.patchCurrentUser,
        onMutate: async ({ operations }) => {
            try {
                await queryClient.cancelQueries({ queryKey: USER_KEYS.all })

                const snapshot = queryClient.getQueryData<RootUser>(USER_KEYS.all)

                queryClient.setQueryData(USER_KEYS.all, (previousUser?: RootUser) => {
                    if (!previousUser) {
                        return undefined
                    }
                    return applyPatch(previousUser, operations).newDocument
                })

                return { snapshots: snapshot }
            } catch (e) {
                sendToSentry(e)
            }
        },
        onError: (error, _o, context) => {
            queryClient.setQueryData(USER_KEYS.all, context?.snapshots)
            handleError(error)
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: USER_KEYS.all })
        },
    })
}

function useUploadProfileImage() {
    const valuationApiClient = getValuationApiClient()
    const handleError = useGlobalMutationErrorHandler()

    const mutation = useMutation({
        mutationFn: valuationApiClient.users.uploadProfileImage,
        onError: handleError,
        meta: {
            invalidates: [USER_KEYS.all],
        },
    })

    return {
        uploadProfileImage: mutation.mutate,
        isUploadingProfileImage: mutation.isPending,
        isProfileImageUploadError: mutation.isError,
    }
}

function useRemoveProfileImage() {
    const valuationApiClient = getValuationApiClient()
    const handleError = useGlobalMutationErrorHandler()

    const mutation = useMutation({
        mutationFn: valuationApiClient.users.deleteProfileImage,
        onError: handleError,
        meta: {
            invalidates: [USER_KEYS.all],
        },
    })

    return {
        removeProfileImage: () => mutation.mutate(),
        isRemovingProfileImage: mutation.isPending,
    }
}

function useGetUsers({ orderId, enabled }: { orderId?: string; enabled?: boolean } = {}) {
    const valuationApiClient = getValuationApiClient()
    const { user: currentUser } = useContext(IAmContext)
    const customerId = getSelectedCustomer()?.uuid

    const { data, isPending, isLoading, isError } = useQuery({
        enabled,
        queryFn: ({ signal }) => valuationApiClient.users.getUserList({ customerId, orderId }, { signal }),
        queryKey: USER_KEYS.list({ customerId, orderId }),
    })

    const usersWithCurrentUserHighlight = useMemo<UserWithCurrentUser<RootUser>[]>(
        () => data?.users.map((user) => ({ ...user, isCurrentUser: user.id === currentUser?.id })) ?? [],
        [data, currentUser]
    )

    const sortedUsers = useMemo(() => usersWithCurrentUserHighlight.toSorted((a, b) => sortUsers(a, b)), [usersWithCurrentUserHighlight])

    return {
        currentUser,
        users: sortedUsers,
        isUsersPending: isPending,
        isUsersInitialLoading: isLoading,
        isUsersError: isError,
    }
}

function useGetCustomerUsers({ orderId, enabled }: { orderId?: string; enabled?: boolean } = {}) {
    const valuationApiClient = getValuationApiClient()
    const { user: currentUser } = useContext(IAmContext)
    const customerId = getSelectedCustomer()?.uuid

    const { data, isPending, isError } = useQuery({
        enabled,
        queryFn: ({ signal }) => valuationApiClient.users.getUserList({ customerId, orderId, userType: 'customer' }, { signal }),
        queryKey: USER_KEYS.list({ customerId, orderId, userType: 'customer' }),
    })

    const usersWithCurrentUserHighlight = useMemo<UserWithCurrentUser<CustomerUser>[]>(
        () => data?.users.map((user) => ({ ...user, isCurrentUser: user.id === currentUser?.id })) ?? [],
        [data, currentUser]
    )

    const sortedUsers = useMemo(() => usersWithCurrentUserHighlight.toSorted((a, b) => sortUsers(a, b)), [usersWithCurrentUserHighlight])

    return {
        currentUser,
        customerUsers: sortedUsers,
        isCustomerUsersPending: isPending,
        isCustomerUsersError: isError,
    }
}

function useGetAppraiserUsers({ orderId, enabled }: { orderId?: string; enabled?: boolean } = {}) {
    const valuationApiClient = getValuationApiClient()
    const { user: currentUser } = useContext(IAmContext)
    const customerId = getSelectedCustomer()?.uuid

    const { data, isPending, isError } = useQuery({
        enabled,
        queryFn: ({ signal }) => valuationApiClient.users.getUserList({ customerId, orderId, userType: 'appraiser' }, { signal }),
        queryKey: USER_KEYS.list({ customerId, orderId, userType: 'appraiser' }),
    })

    const usersWithCurrentUserHighlight = useMemo<UserWithCurrentUser<AppraiserUser>[]>(
        () => data?.users.map((user) => ({ ...user, isCurrentUser: user.id === currentUser?.id })) ?? [],
        [data, currentUser]
    )

    const sortedUsers = useMemo(() => usersWithCurrentUserHighlight.toSorted((a, b) => sortUsers(a, b)), [usersWithCurrentUserHighlight])

    return {
        currentUser,
        appraiserUsers: sortedUsers,
        isAppraiserUsersPending: isPending,
        isAppraiserUsersError: isError,
    }
}

const sortUsers = (a: User, b: User) => {
    // List current user on top
    if (a.isCurrentUser) {
        return -1
    } else if (b.isCurrentUser) {
        return 1
    }
    // Sort by organization name
    if (a.organization_name !== b.organization_name) {
        return a.organization_name.localeCompare(b.organization_name)
    }
    // Inside an organization sort by username or email
    return assembleNameWithEmailFallback(a).localeCompare(assembleNameWithEmailFallback(b))
}

function usePinThread({ pinnedThreads }: { pinnedThreads: string[] }) {
    const { mutate: patchCurrentUser, isPending: isPatchingUser } = usePatchCurrentUser()

    const pinThread = (threadId: string) => {
        patchCurrentUser({
            operations: [
                {
                    op: 'add',
                    path: '/pinned_threads/-',
                    value: threadId,
                },
            ],
        })
    }

    const unpinThread = (threadId: string) => {
        const threadIdIndex = pinnedThreads.indexOf(threadId)
        patchCurrentUser({
            operations: [
                {
                    op: 'remove',
                    path: `/pinned_threads/${threadIdIndex}`,
                },
            ],
        })
    }

    return {
        pinThread,
        unpinThread,
        isPatchingUser,
    }
}

export {
    useGetCurrentUser,
    usePatchCurrentUser,
    useUploadProfileImage,
    useRemoveProfileImage,
    useGetUsers,
    useGetCustomerUsers,
    useGetAppraiserUsers,
    usePinThread,
}
